import type { SVGProps } from 'react';
import { HeartPulse } from 'lucide-react';
import type { CategoryKey } from '@worldcoo/shared/core';

/**
 * The eight glyphs the directory stamps on a category row's dark chip, one per `CategoryKey`.
 *
 * Exported from the hand-off rather than mapped onto Lucide: seven of the eight are Vuesax outlines
 * whose Lucide near-equivalents differ enough to read as a different icon set next to each other
 * (the "teacher" mortarboard and the ISO-style "people" trio have no honest Lucide twin). `health` is
 * the exception — the hand-off's layer is literally "Linear / Medicine / Heart Pulse", so Lucide's
 * `HeartPulse` IS that glyph and there is no reason to inline a copy of it.
 *
 * `stroke="white"` in the export becomes `currentColor` here: the chip is dark today, but the colour
 * belongs to the caller, and a hardcoded white icon is invisible the first time one is placed on a
 * light surface. Every path keeps the export's own geometry on a 16x16 viewBox.
 */

const STROKE = {
	fill: 'none',
	stroke: 'currentColor',
	strokeLinecap: 'round',
	strokeLinejoin: 'round',
} as const;

function Tree(props: SVGProps<SVGSVGElement>) {
	return (
		<svg viewBox="0 0 16 16" aria-hidden {...props}>
			<g {...STROKE}>
				<path d="M10.7802 6.70687H5.22021C4.43354 6.70687 4.16021 6.18021 4.62021 5.54021L7.40022 1.64687C7.72689 1.18021 8.27356 1.18021 8.59356 1.64687L11.3735 5.54021C11.8402 6.18021 11.5669 6.70687 10.7802 6.70687Z" />
				<path d="M11.7269 11.9991H4.28023C3.2269 11.9991 2.86689 11.2991 3.48689 10.4457L6.14689 6.70573H9.86023L12.5202 10.4457C13.1402 11.2991 12.7802 11.9991 11.7269 11.9991Z" />
				<path d="M8 14.6667V12" />
			</g>
		</svg>
	);
}

function Teacher(props: SVGProps<SVGSVGElement>) {
	return (
		<svg viewBox="0 0 16 16" aria-hidden {...props}>
			<g {...STROKE}>
				<path d="M6.69969 1.6875L2.68635 4.3075C1.39969 5.1475 1.39969 7.0275 2.68635 7.8675L6.69969 10.4875C7.41969 10.9608 8.60635 10.9608 9.32635 10.4875L13.3197 7.8675C14.5997 7.0275 14.5997 5.15417 13.3197 4.31417L9.32635 1.69417C8.60635 1.21417 7.41969 1.21417 6.69969 1.6875Z" />
				<path d="M3.75341 8.71875L3.74674 11.8454C3.74674 12.6921 4.40008 13.5987 5.20008 13.8654L7.32674 14.5721C7.69341 14.6921 8.30008 14.6921 8.67341 14.5721L10.8001 13.8654C11.6001 13.5987 12.2534 12.6921 12.2534 11.8454V8.75208" />
				<path d="M14.2669 10V6" />
			</g>
		</svg>
	);
}

function People(props: SVGProps<SVGSVGElement>) {
	return (
		<svg viewBox="0 0 16 16" aria-hidden {...props}>
			<g {...STROKE}>
				<path d="M12 4.77334C11.96 4.76667 11.9133 4.76667 11.8733 4.77334C10.9533 4.74 10.22 3.98667 10.22 3.05333C10.22 2.1 10.9867 1.33333 11.94 1.33333C12.8933 1.33333 13.66 2.10667 13.66 3.05333C13.6533 3.98667 12.92 4.74 12 4.77334Z" />
				<path d="M11.3136 9.62677C12.2269 9.78011 13.2336 9.62011 13.9402 9.14677C14.8802 8.52011 14.8802 7.49344 13.9402 6.86678C13.2269 6.39344 12.2069 6.23344 11.2936 6.39344" />
				<path d="M3.98029 4.77334C4.02029 4.76667 4.06695 4.76667 4.10695 4.77334C5.02695 4.74 5.76028 3.98667 5.76028 3.05333C5.76028 2.1 4.99362 1.33333 4.04029 1.33333C3.08695 1.33333 2.32028 2.10667 2.32028 3.05333C2.32695 3.98667 3.06029 4.74 3.98029 4.77334Z" />
				<path d="M4.66695 9.62667C3.75362 9.78001 2.74695 9.62001 2.04029 9.14667C1.10029 8.52001 1.10029 7.49334 2.04029 6.86667C2.75362 6.39334 3.77362 6.23334 4.68695 6.39334" />
				<path d="M8.00005 9.7525C7.96005 9.74584 7.91339 9.74584 7.87339 9.7525C6.95339 9.71917 6.22005 8.96583 6.22005 8.0325C6.22005 7.07917 6.98672 6.3125 7.94005 6.3125C8.89338 6.3125 9.66005 7.08583 9.66005 8.0325C9.65339 8.96583 8.92005 9.72584 8.00005 9.7525Z" />
				<path d="M6.05982 11.8529C5.11982 12.4796 5.11982 13.5062 6.05982 14.1329C7.12648 14.8462 8.87315 14.8462 9.93982 14.1329C10.8798 13.5062 10.8798 12.4796 9.93982 11.8529C8.87982 11.1462 7.12648 11.1462 6.05982 11.8529Z" />
			</g>
		</svg>
	);
}

function Carrot(props: SVGProps<SVGSVGElement>) {
	return (
		<svg viewBox="0 0 16 16" aria-hidden {...props}>
			<g {...STROKE}>
				<path d="M14.0006 2L11.4756 4.525" />
				<path d="M9.5 3.5C9.5 3.5 11 2 12.5 2.5C13 4 11.5 5.5 11.5 5.5" />
				<path d="M10.9463 5.05371C12.3123 6.41971 12.3123 8.63371 10.9463 9.99971C10.2673 10.6787 3.51929 13.4737 2.73829 13.9377C2.31629 14.1877 1.81229 13.6837 2.06229 13.2617C2.52629 12.4807 5.32129 5.73271 6.00029 5.05371C7.36629 3.68771 9.58029 3.68771 10.9463 5.05371Z" />
			</g>
		</svg>
	);
}

function PersonSimpleCircle(props: SVGProps<SVGSVGElement>) {
	return (
		<svg viewBox="0 0 16 16" aria-hidden {...props}>
			<g {...STROKE}>
				<path d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z" />
				<path d="M5 7H11" />
				<path d="M10 11.5L8 8.5L6 11.5" />
				<path d="M8 7V8.5" />
			</g>
			{/* The head is the one filled mark in the set — a stroked circle this small closes up into a blob. */}
			<path
				d="M8 5.875C8.48325 5.875 8.875 5.48325 8.875 5C8.875 4.51675 8.48325 4.125 8 4.125C7.51675 4.125 7.125 4.51675 7.125 5C7.125 5.48325 7.51675 5.875 8 5.875Z"
				fill="currentColor"
			/>
		</svg>
	);
}

function Warning(props: SVGProps<SVGSVGElement>) {
	return (
		<svg viewBox="0 0 16 16" aria-hidden {...props}>
			<g {...STROKE}>
				<path d="M8 5.16667V8.66667" />
				<path d="M14.0533 5.72002V10.28C14.0533 11.0267 13.6533 11.72 13.0067 12.1L9.04667 14.3867C8.4 14.76 7.59999 14.76 6.94665 14.3867L2.98665 12.1C2.33999 11.7267 1.93998 11.0333 1.93998 10.28V5.72002C1.93998 4.97336 2.33999 4.28 2.98665 3.9L6.94665 1.61333C7.59332 1.24 8.39334 1.24 9.04667 1.61333L13.0067 3.9C13.6533 4.28 14.0533 4.96669 14.0533 5.72002Z" />
				{/* The dot of the "!" — a zero-length stroke, so it needs its own heavier width to render. */}
				<path d="M8 10.7995V10.8661" strokeWidth={1.5} />
			</g>
		</svg>
	);
}

function Drop(props: SVGProps<SVGSVGElement>) {
	return (
		<svg viewBox="0 0 16 16" aria-hidden {...props}>
			{/* The export gives this one a miter join rather than the round one the rest share. */}
			<path
				d="M8.40684 1.47333C8.16684 1.28667 7.83352 1.28667 7.59352 1.47333C6.32686 2.44 2.58685 5.59333 2.60685 9.26667C2.60685 12.24 5.02685 14.6667 8.00685 14.6667C10.9869 14.6667 13.4068 12.2467 13.4068 9.27333C13.4135 5.65333 9.66684 2.44667 8.40684 1.47333Z"
				fill="none"
				stroke="currentColor"
				strokeMiterlimit={10}
			/>
		</svg>
	);
}

/**
 * The whole set, keyed by `CategoryKey` so a row renders `CATEGORY_ICONS[category]` and a new
 * category added to `@worldcoo/shared/core` fails the build here until it is given a glyph.
 */
export const CATEGORY_ICONS: Record<CategoryKey, (props: SVGProps<SVGSVGElement>) => React.ReactNode> = {
	health: HeartPulse,
	environment: Tree,
	education: Teacher,
	habitat: People,
	feeding: Carrot,
	humanRights: PersonSimpleCircle,
	emergency: Warning,
	basicNeeds: Drop,
};
